import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Trash2, Search, User, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import moment from "moment";

const SUBJECT_TYPES = { client: "Client", worker: "Treballador", supplier: "Proveïdor", other: "Altres" };
const SUBJECT_COLORS = { client: "bg-blue-100 text-blue-700", worker: "bg-amber-100 text-amber-700", supplier: "bg-green-100 text-green-700", other: "bg-gray-100 text-gray-600" };
const METHOD_LABELS = { written_form: "Formulari escrit", digital_form: "Formulari digital", verbal: "Verbal", implicit: "Implícit" };

export default function ConsentsList({ items, onEdit, onDeleted, isAdmin }) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const filtered = items.filter(i => {
    const matchSearch = !search || i.subject_name?.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || i.subject_type === typeFilter;
    return matchSearch && matchType;
  });

  const handleDelete = async (id) => {
    await hosys.entities.DataConsent.delete(id);
    onDeleted(id);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Cercar per nom..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Tipus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tots</SelectItem>
            {Object.entries(SUBJECT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <User className="w-10 h-10 mx-auto mb-2 text-gray-200" />
          <p className="text-sm">Sense consentiments registrats</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => (
            <Card key={item.id} className={`p-4 border-0 shadow-sm ${item.withdrawn ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${SUBJECT_COLORS[item.subject_type]}`}>
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">{item.subject_name}</span>
                    <Badge className={`${SUBJECT_COLORS[item.subject_type]} border-0 text-[10px]`}>{SUBJECT_TYPES[item.subject_type]}</Badge>
                    {item.withdrawn
                      ? <Badge className="bg-red-100 text-red-600 border-0 text-[10px]">Retirat {item.withdrawn_date ? moment(item.withdrawn_date).format("DD/MM/YY") : ""}</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">Actiu</Badge>
                    }
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.purpose}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-400">
                    <span>{METHOD_LABELS[item.consent_method]}</span>
                    <span>· {moment(item.consent_date).format("DD/MM/YYYY")}</span>
                    {item.subject_email && <span>· {item.subject_email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {item.document_url && (
                    <a href={item.document_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-blue-400 hover:text-blue-600">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </a>
                  )}
                  {isAdmin && (
                    <>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-[#7B2D3F]" onClick={() => onEdit(item)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="w-7 h-7 text-gray-400 hover:text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}